import { Link } from "react-router-dom";
import { ArrowRight, Handshake, Truck, ClipboardCheck, ShieldCheck } from "lucide-react";
import { useReveal } from "../hooks/use-reveal";
import { IMAGES, COMPANY } from "../data/site";

const VALUE_PROPS = [
    {
        icon: ClipboardCheck,
        title: "Inspector-ready work",
        body: "Code-compliant air sealing, baffles, and insulation depth markers — every job, every time. Zero callbacks for your QC.",
    },
    {
        icon: Truck,
        title: "Show up & finish on schedule",
        body: "We respect your build schedule. Crews arrive when promised and stay until the scope is done — no half-day disappearances.",
    },
    {
        icon: Handshake,
        title: "GC-friendly pricing",
        body: "Volume pricing for production builders, remodelers, and energy auditors. Pre-negotiated SKUs across our 11 services.",
    },
    {
        icon: ShieldCheck,
        title: "Fully licensed & insured",
        body: "Active GL coverage, workers comp, and bonded crews in IL and IN. Certificates emailed within an hour of award.",
    },
];

export default function Contractors() {
    useReveal();
    return (
        <main data-testid="contractors-page" className="pt-24">
            {/* Hero */}
            <section className="relative py-24 md:py-32 bg-ink text-bone overflow-hidden">
                <img
                    src={IMAGES.contractor}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-6 reveal">
                        <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                        For Contractors & GCs
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.98] max-w-4xl reveal">
                        Sub us in. Sleep at night.
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-bone/80 max-w-2xl reveal">
                        Production builders, remodelers, energy auditors — HAZ Insulation is
                        the insulation sub Chicagoland contractors call when reputation
                        matters. 50 years combined experience, scaled from 1 to 13 trucks at the
                        founder&apos;s previous company, zero drama.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
                        <Link
                            to="/#quote"
                            data-testid="contractors-cta-quote"
                            className="btn-lift inline-flex items-center justify-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                        >
                            Request Pricing Sheet
                            <ArrowRight size={18} />
                        </Link>
                        <a
                            href={COMPANY.phoneHref}
                            data-testid="contractors-phone-link"
                            className="btn-lift inline-flex items-center justify-center gap-2 border-2 border-bone text-bone font-semibold py-4 px-8 hover:bg-bone hover:text-ink"
                        >
                            Call {COMPANY.phone}
                        </a>
                    </div>
                </div>
            </section>

            {/* Value props */}
            <section className="py-24 md:py-32 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14 items-end reveal">
                        <h2 className="lg:col-span-7 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                            What you actually get when you{" "}
                            <span className="text-copper">sub us in.</span>
                        </h2>
                        <p className="lg:col-span-5 text-muted_ink leading-relaxed">
                            We&apos;ve been on the GC side. We know the things that wreck a job —
                            and we&apos;ve built our process specifically to not be those things.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
                        {VALUE_PROPS.map((v) => {
                            const Icon = v.icon;
                            return (
                                <div
                                    key={v.title}
                                    data-testid={`contractor-value-${v.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                                    className="bg-white p-10 md:p-12 reveal"
                                >
                                    <Icon
                                        size={32}
                                        strokeWidth={1.5}
                                        className="text-copper mb-6"
                                    />
                                    <h3 className="font-heading text-2xl font-bold mb-3">
                                        {v.title}
                                    </h3>
                                    <p className="text-muted_ink leading-relaxed">{v.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Capabilities */}
            <section className="py-24 md:py-32 bg-moss text-bone relative overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-6 reveal">
                        <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                        Capabilities
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl reveal">
                        We carry the whole envelope — not just the easy bid items.
                    </h2>
                    <ul className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-5 text-bone/85 reveal">
                        {[
                            "Open & closed-cell spray foam",
                            "Dense-pack cellulose",
                            "Blown-in fiberglass",
                            "Batt fiberglass",
                            "Attic insulation systems",
                            "Crawl space encapsulation",
                            "Wall retrofits",
                            "Duct sealing & HVAC support",
                            "Gutter installation",
                            "Varmint removal & exclusion",
                            "Mold remediation",
                            "Bath & attic ventilation",
                        ].map((c) => (
                            <li
                                key={c}
                                className="flex items-start gap-3 border-l border-bone/20 pl-4"
                            >
                                <span className="font-heading">{c}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-16 reveal">
                        <Link
                            to="/#quote"
                            data-testid="contractors-bottom-cta"
                            className="btn-lift inline-flex items-center gap-2 bg-bone text-ink font-semibold py-4 px-8 hover:bg-ember"
                        >
                            Start a Project
                            <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
